import { useTranslations } from '@vocab/react';
import {
  Box,
  Divider,
  Heading,
  IconClear,
  IconTick,
  Inline,
  Stack,
  Text,
} from 'braid-design-system';
import { vars } from 'braid-design-system/css';

import translations from './.vocab';
import type { NewProfileVisibilityConfigType } from './ProfileVisibilityCardGroup';
export const NewProfileVisibilityCard = ({
  profileConfig,
}: {
  profileConfig: NewProfileVisibilityConfigType;
}) => {
  const { t } = useTranslations(translations);
  return (
    <Box
      borderRadius="large"
      overflow="hidden"
      background="surface"
      height="full"
      style={{
        border: '1px solid',
        borderColor: vars.borderColor.neutralLight,
      }}
    >
      <Box
        background={profileConfig.isRecommended ? 'brand' : 'surface'}
        paddingX="small"
        paddingY="medium"
      >
        <Stack space="medium">
          {profileConfig.isRecommended !== undefined && (
            <Inline space="xxsmall">
              <Box
                background={
                  profileConfig.isRecommended ? 'brandAccent' : 'surface'
                }
                paddingX="xsmall"
                paddingY="xxsmall"
                borderRadius="standard"
                style={{
                  minHeight: vars.space.medium,
                }}
              >
                <Text size="xsmall">
                  {profileConfig.isRecommended ? t('Recommended') : ''}
                </Text>
              </Box>
            </Inline>
          )}
          <Heading level="3">{t(profileConfig.type)}</Heading>
          <Text>{t(profileConfig.description)}</Text>
        </Stack>
      </Box>
      {profileConfig.isRecommended === false && (
        <Box paddingX="small">
          <Divider />
        </Box>
      )}

      <Box paddingX="small" paddingY="medium">
        <Stack space="large">
          {profileConfig.settings?.map((setting) => (
            <Stack space="small" key={setting.title}>
              <Text size="small" weight="strong">
                {t(setting.title)}
              </Text>
              {setting.findProfile !== undefined && (
                <BulletItem
                  title="Find your profile"
                  allow={setting.findProfile}
                />
              )}
              {setting.seeBasicProfile !== undefined && (
                <BulletItem
                  title="See basic profile information"
                  allow={setting.seeBasicProfile}
                />
              )}
              {setting.seeProfile !== undefined && (
                <BulletItem
                  title="See your profile"
                  allow={setting.seeProfile}
                />
              )}
              {setting.contactOpportunities !== undefined && (
                <BulletItem
                  title="Contact you with opportunities"
                  allow={setting.contactOpportunities}
                />
              )}
              {setting.verifiedInfo !== undefined && (
                <BulletItem
                  title="See verified information"
                  allow={setting.verifiedInfo}
                />
              )}
              {setting.viewResume !== undefined && (
                <BulletItem
                  title="View your resume"
                  allow={setting.viewResume}
                />
              )}
            </Stack>
          ))}
          {profileConfig.isProfileHidden && (
            <Text size="small">
              {t(
                'Your profile can only be accessed by employers as part of your applications.',
              )}
            </Text>
          )}
        </Stack>
      </Box>
    </Box>
  );
};

const BulletItem = ({
  title,
  allow,
}: {
  title:
    | 'Find your profile'
    | 'See basic profile information'
    | 'See your profile'
    | 'Contact you with opportunities'
    | 'View your resume'
    | 'See verified information';
  allow: boolean;
}) => {
  const { t } = useTranslations(translations);
  return (
    <Box display="flex" flexDirection="row" gap="xsmall" alignItems="center">
      <Box flexGrow={0}>
        {allow ? (
          <IconTick size="xsmall" tone="positive" />
        ) : (
          <IconClear size="xsmall" tone="critical" />
        )}
      </Box>
      <Box flexGrow={1}>
        <Text size="small">{t(title)}</Text>
      </Box>
    </Box>
  );
};
